import {
  Component,
  inject,
  OnInit,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { NgSelectComponent } from '@ng-select/ng-select';
import { ToastrService } from 'ngx-toastr';
import { Subject, of } from 'rxjs';
import {
  catchError,
  debounceTime,
  distinctUntilChanged,
  switchMap,
  tap,
} from 'rxjs/operators';
import { SearchSelectComponent } from '../../../shared/components/search-select/search-select.component';
import { VendorService } from '../vendor.service';

/** One row of the manual add typeahead. */
interface MappableProduct {
  productId: string;
  sku: string;
  name: string;
  alreadyMapped: boolean;
}

@Component({
  selector: 'app-vendor-sku-upload',
  standalone: true,
  imports: [CommonModule, FormsModule, NgSelectComponent, SearchSelectComponent],
  templateUrl: './vendor-sku-upload.component.html',
  styleUrl: './vendor-sku-upload.component.scss',
})
export class VendorSkuUploadComponent implements OnInit {
  private vendorService = inject(VendorService);
  private toastr = inject(ToastrService);

  // Vendors
  vendors: any[] = [];
  selectedVendor: any = null;

  // Unmapped count
  unmappedCount = 0;

  // File upload
  selectedFile: File | null = null;
  fileError = '';

  // Preview state
  isPreviewing = false;
  isUploading = false;
  showPreviewModal = false;
  previewResult: {
    found: number;
    updated: number;
    notFound: string[];
    rows: Array<{ sku: string; status: string }>;
  } | null = null;

  // Manual add — one box searches SKU or name
  productSearch$ = new Subject<string>();
  productOptions: MappableProduct[] = [];
  isSearchingProducts = false;
  selectedProduct: MappableProduct | null = null;
  manualMinOrderQty: number | null = null;
  manualLeadTime: number | null = null;
  isAddingManual = false;

  // Mapped SKUs table
  mappings: any[] = [];
  total = 0;
  page = 1;
  limit = 10;
  isLoadingMappings = false;

  // Mapped SKUs search — one box matches SKU or description
  mappedSearch = '';
  private mappedSearch$ = new Subject<string>();

  // Checkbox selection
  selectedProductIds = new Set<string>();
  allSelected = false;

  // Remove state
  removingProductId: string | null = null;
  isBulkRemoving = false;

  // Export
  isExporting = false;

  ngOnInit(): void {
    this.loadVendors();
    this.loadUnmappedCount();

    this.productSearch$
      .pipe(
        debounceTime(300),
        switchMap((term) => {
          const q = (term || '').trim();
          if (!this.selectedVendor || q.length < 2) {
            this.isSearchingProducts = false;
            return of([] as MappableProduct[]);
          }
          this.isSearchingProducts = true;
          return this.vendorService
            .searchProductsForMapping(this.selectedVendor.id, q)
            .pipe(
              tap(() => (this.isSearchingProducts = false)),
              catchError(() => {
                this.isSearchingProducts = false;
                return of([] as MappableProduct[]);
              }),
            );
        }),
      )
      .subscribe((options) => (this.productOptions = options as MappableProduct[]));

    this.mappedSearch$
      .pipe(debounceTime(300), distinctUntilChanged())
      .subscribe(() => {
        this.page = 1;
        this.loadMappings();
      });
  }

  loadVendors(): void {
    this.vendorService.getAllVendorsList().subscribe({
      next: (vendors) => {
        this.vendors = vendors;
      },
      error: () => {
        this.toastr.error('Failed to load vendors');
      },
    });
  }

  loadUnmappedCount(): void {
    this.vendorService.getUnmappedVendorCount().subscribe({
      next: (res) => {
        this.unmappedCount = res.count;
      },
    });
  }

  onVendorChange(): void {
    this.selectedFile = null;
    this.fileError = '';
    this.previewResult = null;
    this.showPreviewModal = false;
    this.selectedProductIds.clear();
    this.allSelected = false;
    this.page = 1;
    this.mappedSearch = '';
    this.resetManualAdd();
    if (this.selectedVendor) {
      this.loadMappings();
    } else {
      this.mappings = [];
      this.total = 0;
    }
  }

  loadMappings(): void {
    if (!this.selectedVendor) return;
    this.isLoadingMappings = true;
    this.vendorService
      .getSkuMappings(
        this.selectedVendor.id,
        this.page,
        this.limit,
        this.mappedSearch,
      )
      .subscribe({
        next: (res) => {
          this.mappings = res.data;
          this.total = res.total;
          this.isLoadingMappings = false;
          this.syncSelectAll();
        },
        error: () => {
          this.isLoadingMappings = false;
          this.toastr.error('Failed to load mapped SKUs');
        },
      });
  }

  // ─── Manual add ───────────────────────────────────────────────────────────

  addManualMapping(): void {
    if (!this.selectedVendor || !this.selectedProduct || this.isAddingManual)
      return;

    const picked = this.selectedProduct;
    if (picked.alreadyMapped) {
      this.toastr.info(`${picked.sku} is already mapped to this vendor`);
      return;
    }

    this.isAddingManual = true;
    this.vendorService
      .addSkuMapping(this.selectedVendor.id, {
        productId: picked.productId,
        ...(this.manualMinOrderQty != null && this.manualMinOrderQty > 0
          ? { minOrderQty: Math.floor(this.manualMinOrderQty) }
          : {}),
        ...(this.manualLeadTime != null && this.manualLeadTime >= 0
          ? { leadTime: Math.floor(this.manualLeadTime) }
          : {}),
      })
      .subscribe({
        next: (res) => {
          this.isAddingManual = false;
          this.toastr.success(
            res?.created
              ? `${picked.sku} mapped to ${this.selectedVendor.name}`
              : `${picked.sku} mapping updated`,
          );
          this.resetManualAdd();
          this.page = 1;
          this.loadMappings();
          this.loadUnmappedCount();
        },
        error: (err) => {
          this.isAddingManual = false;
          this.toastr.error(err?.error?.message || 'Failed to map SKU');
        },
      });
  }

  private resetManualAdd(): void {
    this.selectedProduct = null;
    this.productOptions = [];
    this.isSearchingProducts = false;
    this.manualMinOrderQty = null;
    this.manualLeadTime = null;
  }

  // ─── Mapped SKUs search ───────────────────────────────────────────────────

  onMappedSearchChange(term: string): void {
    this.mappedSearch = term;
    this.mappedSearch$.next(term);
  }

  clearMappedSearch(): void {
    if (!this.mappedSearch) return;
    this.mappedSearch = '';
    this.page = 1;
    this.loadMappings();
  }

  onFileChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.fileError = '';
    this.selectedFile = null;
    if (!input.files?.length) return;
    const file = input.files[0];
    if (
      !file.name.endsWith('.xlsx') &&
      !file.name.endsWith('.xls')
    ) {
      this.fileError = 'Please select a valid Excel file (.xlsx or .xls)';
      input.value = '';
      return;
    }
    this.selectedFile = file;
  }

  openPreview(): void {
    if (!this.selectedFile || !this.selectedVendor) return;
    this.isPreviewing = true;
    const formData = new FormData();
    formData.append('file', this.selectedFile);
    this.vendorService
      .previewSkuMapping(this.selectedVendor.id, formData)
      .subscribe({
        next: (res) => {
          this.previewResult = res;
          this.showPreviewModal = true;
          this.isPreviewing = false;
        },
        error: (err) => {
          this.isPreviewing = false;
          const msg =
            err?.error?.message || 'Preview failed. Please check your file.';
          this.toastr.error(msg);
        },
      });
  }

  closePreviewModal(): void {
    this.showPreviewModal = false;
  }

  confirmUpload(): void {
    if (!this.selectedFile || !this.selectedVendor) return;
    this.isUploading = true;
    const formData = new FormData();
    formData.append('file', this.selectedFile);
    this.vendorService
      .uploadSkuMapping(this.selectedVendor.id, formData)
      .subscribe({
        next: (res) => {
          this.isUploading = false;
          this.showPreviewModal = false;
          this.selectedFile = null;
          // Reset file input
          const input = document.getElementById('skuFileInput') as HTMLInputElement;
          if (input) input.value = '';

          const notFoundCount = res.notFound?.length || 0;
          if (res.upserted > 0 && notFoundCount > 0) {
            this.toastr.success(
              `${res.upserted} SKUs mapped. ${notFoundCount} SKUs are not in the Product Master — add them first, then upload the file again.`,
            );
          } else if (res.upserted > 0 && res.updated === 0) {
            this.toastr.success(
              `${res.upserted} SKUs mapped to ${this.selectedVendor.name}`,
            );
          } else if (res.updated > 0 && res.upserted === 0) {
            this.toastr.success(
              `All ${res.updated} SKUs updated for ${this.selectedVendor.name}`,
            );
          } else if (res.upserted > 0 || res.updated > 0) {
            this.toastr.success(
              `${res.upserted} SKUs mapped, ${res.updated} updated for ${this.selectedVendor.name}`,
            );
          } else {
            this.toastr.warning(
              'None of the SKUs in this file exist in the Product Master. Add the SKUs in the Product Master first, then upload the file again.',
            );
          }

          this.page = 1;
          this.loadMappings();
          this.loadUnmappedCount();
        },
        error: (err) => {
          this.isUploading = false;
          const msg = err?.error?.message || 'Upload failed. Please try again.';
          this.toastr.error(msg);
        },
      });
  }

  downloadTemplate(): void {
    this.vendorService.downloadSkuMappingTemplate().subscribe({
      next: (blob) => {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'vendor_sku_mapping_template.xlsx';
        a.click();
        window.URL.revokeObjectURL(url);
      },
      error: () => {
        this.toastr.error('Failed to download template');
      },
    });
  }

  exportMappings(): void {
    if (!this.selectedVendor) return;
    this.isExporting = true;
    this.vendorService.exportSkuMappings(this.selectedVendor.id).subscribe({
      next: (blob) => {
        this.isExporting = false;
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `sku_mapping_${this.selectedVendor.name}.xlsx`;
        a.click();
        window.URL.revokeObjectURL(url);
      },
      error: () => {
        this.isExporting = false;
        this.toastr.error('Export failed');
      },
    });
  }

  removeMapping(productId: string): void {
    if (!this.selectedVendor) return;
    this.removingProductId = productId;
    this.vendorService
      .removeSkuMapping(this.selectedVendor.id, productId)
      .subscribe({
        next: () => {
          this.removingProductId = null;
          this.toastr.success('Mapping removed');
          this.selectedProductIds.delete(productId);
          if (this.mappings.length === 1 && this.page > 1) {
            this.page--;
          }
          this.loadMappings();
          this.loadUnmappedCount();
        },
        error: (err) => {
          this.removingProductId = null;
          this.toastr.error(err?.error?.message || 'Remove failed');
        },
      });
  }

  bulkRemove(): void {
    if (!this.selectedVendor || this.selectedProductIds.size === 0) return;
    this.isBulkRemoving = true;
    const ids = Array.from(this.selectedProductIds);
    this.vendorService
      .bulkRemoveSkuMappings(this.selectedVendor.id, ids)
      .subscribe({
        next: (res) => {
          this.isBulkRemoving = false;
          this.toastr.success(`${res.deleted} mapping(s) removed`);
          if (res.skippedPrimary) {
            this.toastr.info(
              `${res.skippedPrimary} skipped: primary vendor on the product master`,
            );
          }
          this.selectedProductIds.clear();
          this.allSelected = false;
          if (this.mappings.length <= ids.length && this.page > 1) {
            this.page--;
          }
          this.loadMappings();
          this.loadUnmappedCount();
        },
        error: () => {
          this.isBulkRemoving = false;
          this.toastr.error('Bulk remove failed');
        },
      });
  }

  toggleRow(productId: string, checked: boolean): void {
    if (checked) {
      this.selectedProductIds.add(productId);
    } else {
      this.selectedProductIds.delete(productId);
    }
    this.syncSelectAll();
  }

  toggleAll(checked: boolean): void {
    this.allSelected = checked;
    if (checked) {
      this.mappings.forEach((m) => this.selectedProductIds.add(m.productId));
    } else {
      this.mappings.forEach((m) => this.selectedProductIds.delete(m.productId));
    }
  }

  isRowSelected(productId: string): boolean {
    return this.selectedProductIds.has(productId);
  }

  private syncSelectAll(): void {
    this.allSelected =
      this.mappings.length > 0 &&
      this.mappings.every((m) => this.selectedProductIds.has(m.productId));
  }

  goToPage(p: number): void {
    if (p < 1 || p > this.totalPages) return;
    this.page = p;
    this.loadMappings();
  }

  get totalPages(): number {
    return Math.ceil(this.total / this.limit);
  }

  get pageNumbers(): number[] {
    const pages: number[] = [];
    const max = this.totalPages;
    const start = Math.max(1, this.page - 2);
    const end = Math.min(max, this.page + 2);
    for (let i = start; i <= end; i++) pages.push(i);
    return pages;
  }

  get previewCanConfirm(): boolean {
    if (!this.previewResult) return false;
    return this.previewResult.found > 0 || this.previewResult.updated > 0;
  }
}
